/**
 * Full screen for one element, shared by the two film players.
 *
 * The identity check against `document.fullscreenElement` matters: the event
 * is global, so without it every player on the page would think it had gone
 * full screen when any of them did.
 */

import { useCallback, useEffect, useState, type RefObject } from 'react';

export function useFullscreen(ref: RefObject<HTMLElement | null>): [boolean, () => void] {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [ref]);

  const toggle = useCallback(() => {
    // Only exit when WE are the one in full screen; if something else is,
    // this button should still put us there rather than just closing them
    // and leaving the user to click again.
    if (document.fullscreenElement === ref.current) {
      void document.exitFullscreen().catch(() => undefined);
    } else {
      void ref.current?.requestFullscreen().catch(() => undefined);
    }
  }, [ref]);

  return [isFullscreen, toggle];
}
